export interface IAppConfigs {
    apiGatewayUrl: string;
    apiGatewayUrlInternal: string;
    adminAppUrl: string;
    stripe: {
        publishableKey: string;
    };
    pusher: {
        appKey: string;
        appCluster: string;
        appHost: string;
        appPort?: string;
        forceTLS: boolean;
    };
    appEnv: string;
    // partners
    partyGamePlugUid: string;
    partyPlugApiKey: string;
}

// RR7-safe env access. Next inlined `process.env.NEXT_PUBLIC_*` into the browser
// bundle; Vite does not. Instead, the public (NEXT_PUBLIC_*) vars are exposed to
// the client via `import.meta.env` (see `envPrefix` in vite.config.ts), while the
// server reads them from `process.env`. Server-only secrets (no NEXT_PUBLIC_
// prefix) are never exposed to the client and resolve to undefined there.
const viteEnv =
    (typeof import.meta !== "undefined" &&
        (import.meta as { env?: Record<string, string | undefined> }).env) ||
    {};
const procEnv = typeof process !== "undefined" && process.env ? process.env : {};

function readEnv(key: string): string | undefined {
    return viteEnv[key] ?? procEnv[key];
}

export const appConfigs: IAppConfigs = {
    apiGatewayUrl: readEnv("NEXT_PUBLIC_USER_GATEWAY_URL")!,
    // Server-side (SSR) gateway base URL. In the dev container `localhost` is the
    // container itself, not the host where the gateway is published, so SSR loaders
    // must use an internal URL (e.g. http://host.docker.internal:6300). Falls back
    // to the public URL — in staging/prod server and browser share one public gateway.
    apiGatewayUrlInternal:
        readEnv("USER_GATEWAY_URL_INTERNAL") ?? readEnv("NEXT_PUBLIC_USER_GATEWAY_URL")!,
    adminAppUrl: readEnv("NEXT_PUBLIC_ADMIN_APP_URL") ?? "", // e.g. http://localhost:3000 for live preview from kn-client-web
    stripe: {
        publishableKey: readEnv("NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY")!,
    },
    pusher: {
        appKey: readEnv("NEXT_PUBLIC_PUSHER_APP_KEY")!,
        appCluster: readEnv("NEXT_PUBLIC_PUSHER_APP_CLUSTER")!,
        appHost: readEnv("NEXT_PUBLIC_PUSHER_HOST")!,
        // Optional ws port for self-hosted Soketi (local dev = 6001); cloud omits it.
        appPort: readEnv("NEXT_PUBLIC_PUSHER_PORT"),
        // TLS on by default; local Soketi is plain ws, so NEXT_PUBLIC_PUSHER_USE_TLS=false.
        forceTLS: readEnv("NEXT_PUBLIC_PUSHER_USE_TLS") !== "false",
    },
    appEnv: readEnv("NEXT_PUBLIC_APP_ENV")!,
    partyGamePlugUid: readEnv("PARTY_PLUG_UID")!,
    partyPlugApiKey: readEnv("PARTY_PLUG_API_KEY")!,
};

export function getAppConfig<T = string>(key: keyof typeof appConfigs): T {
    return appConfigs[key] as T;
}
